/**
 * MessageBubble — пузырь сообщения user или assistant.
 *
 * Props:
 *   role        — 'user' | 'assistant'
 *   content     — string (рендер plain-text, white-space pre-wrap)
 *   sources     — (опц.) RAG-источники под bubble. Элемент — строка (legacy)
 *                 ИЛИ объект { label, href }. Если href задан — чип кликабельный
 *                 (ведёт на статью базы знаний через onSourceClick).
 *   onSourceClick — (опц.) (href) => void. Клик по источнику-ссылке; вызывается
 *                 вместо штатной навигации <a> (e.preventDefault внутри).
 *   showRole    — boolean (опц., по умолчанию НЕ показываем role-label —
 *                 визуал и так очевиден; включать только если нужно).
 *   collapsible — boolean (опц.). Если true И role === 'assistant' — длинный
 *                 ответ рендерится в стиле «Gemini Daily Brief»: первая 1-2
 *                 фразы как bold-резюме, остальное прячется под «Подробнее».
 *                 Короткие ответы (нет осмысленного «хвоста») рендерятся как есть.
 *
 * Markdown намеренно НЕ рендерим — это shared-примитив, тяжёлый markdown
 * сидит в специфичных wrapper'ах (например, WikiAIChat имеет свой sanitizer).
 * Если потребуется markdown — добавим prop renderContent={(c) => <ReactMarkdown/>}.
 */

import { useState } from 'react'
import { ChevronDown, ChevronUp, FileText } from 'lucide-react'

// Минимальная длина «хвоста», ниже которой сворачивать смысла нет.
const MIN_DETAIL_CHARS = 40
// Не уводить bold-резюме в простыню: режем по абзацу, если он близко.
const MAX_LEAD_CHARS = 240

/**
 * Разбить ответ на bold-резюме (lead) и сворачиваемый остаток (rest).
 * Приоритет границы: первый абзац (\n\n) → конец 2-й фразы → нет разбивки.
 */
export function splitLead(text) {
    const t = (text || '').trim()
    if (!t) return { lead: '', rest: '' }

    let cut = -1
    const para = t.indexOf('\n\n')
    if (para > 0 && para <= MAX_LEAD_CHARS) {
        cut = para
    } else {
        const re = /[.!?…](?=\s|$)/g
        const ends = []
        let m
        while ((m = re.exec(t)) !== null) ends.push(re.lastIndex)
        if (ends.length >= 2) cut = ends[1]
    }

    if (cut < 0) return { lead: t, rest: '' }
    const lead = t.slice(0, cut).trim()
    const rest = t.slice(cut).trim()
    if (rest.length < MIN_DETAIL_CHARS) return { lead: t, rest: '' }
    return { lead, rest }
}

export default function MessageBubble({
    role,
    content,
    sources = null,
    showRole = false,
    collapsible = false,
    onSourceClick = null,
}) {
    const isUser = role === 'user'
    const [expanded, setExpanded] = useState(false)

    const split = collapsible && !isUser ? splitLead(content) : null
    const hasCollapse = Boolean(split && split.rest)

    return (
        <>
            {showRole ? (
                <div
                    className={
                        'conversation__role' +
                        (isUser ? ' conversation__role--user' : '')
                    }
                >
                    {isUser ? 'Ты' : 'AI'}
                </div>
            ) : null}
            <div
                className={
                    'conversation__bubble ' +
                    (isUser
                        ? 'conversation__bubble--user'
                        : 'conversation__bubble--assistant')
                }
            >
                {hasCollapse ? (
                    <>
                        <div className="conversation__summary">{split.lead}</div>
                        {expanded ? (
                            <div className="conversation__detail">{split.rest}</div>
                        ) : null}
                        <button
                            type="button"
                            className="conversation__more-btn"
                            onClick={() => setExpanded((e) => !e)}
                            aria-expanded={expanded}
                        >
                            {expanded ? (
                                <ChevronUp size={13} aria-hidden="true" />
                            ) : (
                                <ChevronDown size={13} aria-hidden="true" />
                            )}
                            {expanded ? 'Свернуть' : 'Подробнее'}
                        </button>
                    </>
                ) : (
                    content
                )}
                {sources && sources.length > 0 ? (
                    <div className="conversation__sources">
                        {sources.map((s, i) => {
                            // source — строка (legacy) ИЛИ { label, href }.
                            const label = typeof s === 'string' ? s : s?.label
                            const href = typeof s === 'string' ? null : s?.href
                            if (!label) return null
                            if (href) {
                                return (
                                    <a
                                        key={i}
                                        href={href}
                                        className="conversation__source conversation__source--link"
                                        onClick={(e) => {
                                            if (onSourceClick) {
                                                e.preventDefault()
                                                onSourceClick(href)
                                            }
                                        }}
                                    >
                                        <FileText size={11} aria-hidden="true" />
                                        {label}
                                    </a>
                                )
                            }
                            return (
                                <span key={i} className="conversation__source">
                                    <FileText size={11} aria-hidden="true" />
                                    {label}
                                </span>
                            )
                        })}
                    </div>
                ) : null}
            </div>
        </>
    )
}
